import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpSimplifiedErrorResponse } from 'src/app/core/interfaces/http-simplified-error-response';
import {
  AutoTagFetchResult,
  IAutoTagDecision,
  IAutoTagDecisionsRequest,
  IAutoTagDecisionsResponse,
  IAutoTagLink,
  IAutoTagLinksResponse,
  IAutoTagRunRequest,
  IAutoTagRunResponse,
  IAutoTagRunTriggerResult,
  IAutoTagSuggestionsResponse,
} from 'src/app/core/models/i-auto-tag-responses';
import { RestService } from './rest-service';

export type AutoTagFixtureFlag = 'empty' | 'error';

@Injectable({
  providedIn: 'root',
})
export class AutoTaggingService {
  // Domain-scoped route segment for the cms-api smart-linking proxy: api/smart-linking/{domain}/...
  static readonly ENTITY_IDENTIFIER: string = 'smart-linking/auto_tagging';
  private readonly BASE_AUTO_TAGGING_ROUTE = `api/${AutoTaggingService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_AUTO_TAGGING_ROUTE, ...args);

  constructor(private restService: RestService) {}

  triggerRun$(
    articleId: string,
    body: IAutoTagRunRequest,
    options?: { fixture?: AutoTagFixtureFlag },
  ): Observable<IAutoTagRunTriggerResult> {
    const httpOptions = options?.fixture
      ? {
          params: this.restService.buildHttpParamsObject({
            fixture: options.fixture,
          }),
        }
      : {};

    return this.restService
      .post$<IAutoTagRunResponse>(
        this.BUILD_HTTP_ROUTE('articles', articleId, 'runs'),
        body,
        httpOptions,
      )
      .pipe(
        map((response) => ({
          runId: response.run_id,
          wasAlreadyRunning: response.was_already_running,
        })),
        catchError((error: HttpSimplifiedErrorResponse) => {
          const errorBody = this.errorBody<{ error: string; run_id: string }>(
            error,
          );
          if (error.status === 409 && errorBody?.error === 'run_in_flight') {
            return of({ runId: errorBody.run_id, wasAlreadyRunning: true });
          }
          return throwError(error);
        }),
      );
  }

  getAutoTags$(articleId: string): Observable<AutoTagFetchResult> {
    return this.restService
      .get$<IAutoTagSuggestionsResponse>(
        this.BUILD_HTTP_ROUTE('articles', articleId, 'suggestions'),
      )
      .pipe(
        map((response): AutoTagFetchResult => {
          if (response.status === 'complete') {
            return {
              kind: 'complete',
              runId: response.run_id,
              contentHash: response.content_hash,
              result: response.result,
            };
          }
          if (response.status === 'failed') {
            return { kind: 'failed', runId: response.run_id };
          }
          if (response.status === 'running') {
            return { kind: 'running', runId: response.run_id ?? '' };
          }
          return { kind: 'no_run' };
        }),
        catchError((error: HttpSimplifiedErrorResponse) => {
          const errorBody = this.errorBody<{ error: string }>(error);
          if (error.status === 404 && errorBody?.error === 'no_run') {
            return of<AutoTagFetchResult>({ kind: 'no_run' });
          }
          return throwError(error);
        }),
      );
  }

  // The AI-linked taxonomy items for this article (persisted in the links table). Drives the
  // "added by AI" icon durably. Best-effort: an error yields no links rather than blocking load.
  getLinks$(articleId: string): Observable<IAutoTagLink[]> {
    return this.restService
      .get$<IAutoTagLinksResponse>(
        this.BUILD_HTTP_ROUTE('articles', articleId, 'links'),
      )
      .pipe(
        map((response) => response?.links ?? []),
        catchError(() => of<IAutoTagLink[]>([])),
      );
  }

  // Report acceptance/removal of auto-tag suggestions. Best-effort: swallow errors so telemetry
  // never breaks the journalist's flow.
  submitDecisions$(
    articleId: string,
    runId: string,
    decisions: IAutoTagDecision[],
  ): Observable<void> {
    const body: IAutoTagDecisionsRequest = { run_id: runId, decisions };
    return this.restService
      .post$<IAutoTagDecisionsResponse>(
        this.BUILD_HTTP_ROUTE('articles', articleId, 'decisions'),
        body,
      )
      .pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  // errorMessage is typed string but carries the parsed JSON body when the server returns one.
  // Guarded so an HTML error page rethrows instead of half-matching a contract error.
  private errorBody<T extends { error: string }>(
    error: HttpSimplifiedErrorResponse,
  ): T | undefined {
    const body = error?.errorMessage as unknown;
    return typeof body === 'object' && body !== null ? (body as T) : undefined;
  }
}
